import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import { useFramerAnimation } from '../../hooks';

const StyledCol = styled(motion.div)`
  ${(props) =>
    props.isBgColor
      ? `
        border-radius: 0.75rem !important;
        --tw-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1),
          0 8px 10px -6px rgb(0 0 0 / 0.1);
        --tw-shadow-colored: 0 20px 25px -5px var(--tw-shadow-color),
          0 8px 10px -6px var(--tw-shadow-color);
        box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000),
          var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow);
    `
      : ''}
  padding: ${(props) => props.$padding} !important;
  color: ${(props) => props.$color} !important;
  background-color: ${(props) =>
    props.isBgColor
      ? `${props.$bgColor} !important`
      : 'transparent !important'};
  h2 {
    color: ${(props) => props.$h2Color} !important;
  }
  &:hover {
    color: ${(props) => props.$colorHover} !important;
    background-color: ${(props) => props.$bgColorHover} !important;
  }
`;

const Col = ({
  children,
  classes,
  isBgColor,
  padding,
  color,
  bgColor,
  h2Color,
  initialOpacity,
  initialScale,
  animateOpacity,
  animateScale,
  transitionDuration,
  transitionDelay,
  inViewConfigTriggerOnce,
  inViewConfigMargin,
  disableAnimations,
}) => {
  const { variants, controls, ref } = useFramerAnimation({
    initial: disableAnimations
      ? { opacity: 1, scale: 1 }
      : { opacity: initialOpacity, scale: initialScale },
    animate: disableAnimations
      ? { opacity: 1, scale: 1 }
      : { opacity: animateOpacity, scale: animateScale },
    transition: { duration: transitionDuration, delay: transitionDelay },
    inViewConfig: {
      once: inViewConfigTriggerOnce,
      margin: inViewConfigMargin,
    },
    disableAnimations,
  });

  return (
    <StyledCol
      className={classes}
      $padding={padding}
      $color={color}
      $bgColor={bgColor}
      $h2Color={h2Color}
      isBgColor={isBgColor}
      ref={ref}
      variants={variants}
      initial="initial"
      animate={controls}
      initialOpacity={initialOpacity}
      initialScale={initialScale}
      animateOpacity={animateOpacity}
      animateScale={animateScale}
      transitionDuration={transitionDuration}
      transitionDelay={transitionDelay}
      inViewConfigTriggerOnce={inViewConfigTriggerOnce}
      inViewConfigMargin={inViewConfigMargin}
      disableAnimations={disableAnimations}
    >
      {children}
    </StyledCol>
  );
};

Col.defaultProps = {
  classes: ``,
  padding: '',
  color: '',
  h2Color: '',
  bgColor: 'transparent',
  isBgColor: false,
  initialOpacity: 0.1,
  initialScale: 0.8,
  animateOpacity: 1,
  animateScale: 1,
  transitionDuration: 0.5,
  transitionDelay: 0,
  inViewConfigTriggerOnce: true,
  inViewConfigMargin: `0%`,
  disableAnimations: false,
};

Col.propTypes = {
  padding: PropTypes.string,
  classes: PropTypes.string,
  bgColor: PropTypes.string,
  isBgColor: PropTypes.bool,
  children: PropTypes.node.isRequired,
  initialOpacity: PropTypes.number,
  initialScale: PropTypes.number,
  animateOpacity: PropTypes.number,
  animateScale: PropTypes.number,
  transitionDuration: PropTypes.number,
  transitionDelay: PropTypes.number,
  inViewConfigTriggerOnce: PropTypes.bool,
  inViewConfigMargin: PropTypes.string,
  disableAnimations: PropTypes.bool,
};

export { Col };
